import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box
} from '@mui/material'

type Props = {
  authenticated: boolean
  accessTokenExpiresAt?: number | null
  refreshTokenExpiresAt?: number | null
  onStayLoggedIn: () => Promise<void>
  onLogout: () => Promise<void>
}

const WARNING_WINDOW_MS = 30_000

const SessionTimeoutWarning: React.FC<Props> = ({
  authenticated,
  accessTokenExpiresAt,
  refreshTokenExpiresAt,
  onStayLoggedIn,
  onLogout
}) => {
  const [open, setOpen] = React.useState(false)
  const [secondsLeft, setSecondsLeft] = React.useState<number | null>(null)
  const warningTimerRef = React.useRef<number | null>(null)
  const countdownTimerRef = React.useRef<number | null>(null)

  const clearTimers = React.useCallback(() => {
    if (warningTimerRef.current) {
      window.clearTimeout(warningTimerRef.current)
      warningTimerRef.current = null
    }
    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
  }, [])

  const getExpiry = () => refreshTokenExpiresAt ?? accessTokenExpiresAt ?? null

  const startCountdown = React.useCallback((expiresAt: number) => {
    clearTimers()
    const update = () => {
      const deltaMs = Math.max(0, expiresAt - Date.now())
      const nextSeconds = Math.ceil(deltaMs / 1000)
      setSecondsLeft(nextSeconds)
      if (nextSeconds <= 0) {
        setOpen(false)
        onLogout().catch(() => undefined)
      }
    }
    update()
    countdownTimerRef.current = window.setInterval(update, 1000)
  }, [clearTimers, onLogout])

  React.useEffect(() => {
    clearTimers()
    setOpen(false)
    setSecondsLeft(null)

    if (!authenticated) {
      return
    }

    const expiresAt = getExpiry()
    if (!expiresAt) {
      return
    }

    const warnAt = expiresAt - WARNING_WINDOW_MS
    const now = Date.now()

    if (warnAt <= now) {
      setOpen(true)
      startCountdown(expiresAt)
      return
    }

    warningTimerRef.current = window.setTimeout(() => {
      setOpen(true)
      startCountdown(expiresAt)
    }, warnAt - now)

    return clearTimers
  }, [authenticated, accessTokenExpiresAt, refreshTokenExpiresAt, clearTimers, startCountdown])

  const handleStayLoggedIn = async () => {
    try {
      await onStayLoggedIn()
    } finally {
      setOpen(false)
      setSecondsLeft(null)
    }
  }

  if (!open) {
    return null
  }

  return (
    <Dialog open={open} onClose={() => undefined} aria-labelledby="session-timeout-title">
      <DialogTitle id="session-timeout-title">Session läuft gleich ab</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Deine Anmeldung läuft gleich ab.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6">
            {secondsLeft !== null ? `${secondsLeft}s` : '—'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={() => onLogout().catch(() => undefined)}>
          Abmelden
        </Button>
        <Button variant="contained" onClick={handleStayLoggedIn}>
          Eingeloggt bleiben
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SessionTimeoutWarning
